import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Camera, Video, Upload, Circle, Square } from 'lucide-react';

const VIDEO_DURATION_LIMIT = 60; // 60 segundos

const MediaCaptureScreen = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const mode = location.state?.mode || 'photo'; // 'photo' ou 'video'

    const videoRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const [isRecording, setIsRecording] = useState(false);
    const [time, setTime] = useState(0);
    const [stream, setStream] = useState(null);

    const stopStream = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    }, [stream]);

    useEffect(() => {
        const getMedia = async () => {
            try {
                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' },
                    audio: mode === 'video'
                });
                setStream(mediaStream);
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
            } catch (err) {
                console.error("Erro ao acessar a câmera:", err);
                alert("Não foi possível acessar a câmera. Verifique as permissões do seu navegador.");
                navigate(-1);
            }
        };
        getMedia();
        return stopStream;
    }, [mode, navigate, stopStream]);

    useEffect(() => {
        let timer;
        if (isRecording) {
            timer = setInterval(() => {
                setTime(prevTime => {
                    if (prevTime >= VIDEO_DURATION_LIMIT) {
                        handleStopRecording();
                        return prevTime;
                    }
                    return prevTime + 1;
                });
            }, 1000);
        } else {
            clearInterval(timer);
        }
        return () => clearInterval(timer);
    }, [isRecording]);

    const handleStartRecording = () => {
        if (videoRef.current && stream) {
            mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'video/webm' });
            const chunks = [];
            mediaRecorderRef.current.ondataavailable = (e) => chunks.push(e.data);
            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunks, { type: 'video/webm' });
                console.log("Vídeo gravado:", blob);
                // Lógica para enviar o vídeo virá aqui
                stopStream();
                navigate(-1); // Volta para o chat
            };
            mediaRecorderRef.current.start();
            setIsRecording(true);
            setTime(0);
        }
    };

    const handleStopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const handleTakePhoto = () => {
        if (videoRef.current) {
            const canvas = document.createElement('canvas');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
            const dataUrl = canvas.toDataURL('image/jpeg');
            console.log("Foto tirada:", dataUrl.substring(0, 50) + '...');
            // Lógica para enviar a foto virá aqui
            stopStream();
            navigate(-1); // Volta para o chat
        }
    };

    const renderControls = () => {
        if (mode === 'video') {
            return isRecording ? (
                <button onClick={handleStopRecording} className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                    <Square className="w-8 h-8 text-white" />
                </button>
            ) : (
                <button onClick={handleStartRecording} className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <Circle className="w-12 h-12 text-red-500" fill="red" />
                </button>
            );
        }
        return (
            <button onClick={handleTakePhoto} className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                <Camera className="w-10 h-10 text-gray-700" />
            </button>
        );
    };

    return (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
            <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />

            <button onClick={() => { stopStream(); navigate(-1); }} className="absolute top-4 left-4 p-3 bg-black bg-opacity-50 rounded-full text-white">
                <X className="w-6 h-6" />
            </button>

            {isRecording && (
                <div className="absolute top-4 right-4 flex items-center bg-red-500 text-white px-3 py-1 rounded-full">
                    <Circle fill="white" className="w-2 h-2 mr-2 animate-pulse" />
                    <span>{`0:${String(time).padStart(2, '0')}`}</span>
                </div>
            )}

            <div className="absolute bottom-10 left-0 right-0 flex justify-center items-center">
                {renderControls()}
            </div>
        </div>
    );
};

export default MediaCaptureScreen;
